import { NavigateOptions } from "next/dist/shared/lib/app-router-context.shared-runtime"
import { useRouter } from "next/navigation"
import { useEffect, useState, useTransition } from "react"

type PromiseRouter = {
  refresh: () => Promise<void>
  push: (href: string, options?: NavigateOptions | undefined) => Promise<void>
  replace: (
    href: string,
    options?: NavigateOptions | undefined
  ) => Promise<void>
  back: () => Promise<void>
  forward: () => Promise<void>
}

/**
 * Wrapper around any router function from `next/navigation` `useRouter()` to return Promise,
 * and resolve after that function is completed.
 *
 * @returns The function you specified.
 *
 */
export function usePromiseRouter(): PromiseRouter {
  const router = useRouter()
  const [isPending, startTransition] = useTransition()
  const [resolve, setResolve] = useState<((value: unknown) => void) | null>(
    null
  )

  const wrapRouterMethod = <T extends unknown[]>(
    method: (...args: T) => void
  ) => {
    return (...args: T) => {
      return new Promise<void>((resolve) => {
        setResolve(() => resolve)
        startTransition(() => {
          method(...args)
        })
      })
    }
  }

  useEffect(() => {
    if (!isPending && resolve) {
      resolve(null)
      setResolve(null)
    }
  }, [isPending, resolve])

  return {
    refresh: wrapRouterMethod(router.refresh),
    push: wrapRouterMethod(router.push),
    replace: wrapRouterMethod(router.replace),
    back: wrapRouterMethod(router.back),
    forward: wrapRouterMethod(router.forward),
  }
}
